import * as TWEEN from 'three/addons/libs/tween.module.js';
import type { Object3D } from 'three';

const group = new TWEEN.Group();

export interface Vec3Target {
  x?: number;
  y?: number;
  z?: number;
}

export function tweenTo(obj: Object3D, target: Vec3Target, durationMs = 220): TWEEN.Tween<Object3D['position']> {
  return new TWEEN.Tween(obj.position, group)
    .to(
      {
        x: target.x ?? obj.position.x,
        y: target.y ?? obj.position.y,
        z: target.z ?? obj.position.z,
      },
      durationMs,
    )
    .easing(TWEEN.Easing.Cubic.Out)
    .start();
}

export function tweenArc(
  obj: Object3D,
  target: { x: number; y: number; z: number },
  durationMs = 220,
  peakLift = 0.3,
): TWEEN.Tween<{ t: number }> {
  const start = { x: obj.position.x, y: obj.position.y, z: obj.position.z };
  const proxy = { t: 0 };
  return new TWEEN.Tween(proxy, group)
    .to({ t: 1 }, durationMs)
    .easing(TWEEN.Easing.Cubic.Out)
    .onUpdate(() => {
      const t = proxy.t;
      obj.position.x = start.x + (target.x - start.x) * t;
      obj.position.z = start.z + (target.z - start.z) * t;
      const baseY = start.y + (target.y - start.y) * t;
      obj.position.y = baseY + peakLift * Math.sin(Math.PI * t);
    })
    .onComplete(() => {
      obj.position.set(target.x, target.y, target.z);
    })
    .start();
}

export function tweenRotation(
  obj: Object3D,
  target: Vec3Target,
  durationMs = 220,
): TWEEN.Tween<Object3D['rotation']> {
  const to: Record<string, number> = {};
  if (target.x !== undefined) to.x = target.x;
  if (target.y !== undefined) to.y = target.y;
  if (target.z !== undefined) to.z = target.z;
  return new TWEEN.Tween(obj.rotation, group)
    .to(to, durationMs)
    .easing(TWEEN.Easing.Cubic.Out)
    .start();
}

function shortestAngle(from: number, to: number): number {
  let d = to - from;
  while (d > Math.PI) d -= 2 * Math.PI;
  while (d < -Math.PI) d += 2 * Math.PI;
  return d;
}

export function flipTween(
  obj: Object3D,
  target: { x: number; y: number; z: number },
  targetRotZ: number,
  durationMs = 380,
  cardWidth = 1,
): TWEEN.Tween<{ t: number }> {
  const start = {
    x: obj.position.x,
    y: obj.position.y,
    z: obj.position.z,
    rotZ: obj.rotation.z,
  };
  const dx = target.x - start.x;
  const sign = dx >= 0 ? 1 : -1;
  // For the pivot edge to stay planted on the table, the group's rotation
  // must track the angular position of the card-center around the pivot.
  // Rightward sweep (sign=+1): rot goes π → 0 (so dRotZ=-π).
  // Leftward sweep (sign=-1): rot goes π → 2π (so dRotZ=+π).
  const dRotZ = -sign * Math.PI;
  const r = cardWidth / 2;
  // Pivot is the leading edge of the card at its starting position.
  const pivotX = start.x + sign * r;
  // After a pure 180° flip around that edge, the center lands one card-width away.
  const flipEndX = start.x + sign * cardWidth;
  const remainderX = target.x - flipEndX;
  const dy = target.y - start.y;
  const dz = target.z - start.z;
  const proxy = { t: 0 };
  return new TWEEN.Tween(proxy, group)
    .to({ t: 1 }, durationMs)
    .easing(TWEEN.Easing.Linear.None)
    .onUpdate(() => {
      const t = proxy.t;
      const phi = sign === 1 ? Math.PI * (1 - t) : Math.PI * t;
      const localX = r * Math.cos(phi);
      const localY = r * Math.sin(phi);
      obj.position.x = pivotX + localX + remainderX * t;
      obj.position.y = start.y + localY + dy * t;
      obj.position.z = start.z + dz * t;
      obj.rotation.z = start.rotZ + dRotZ * t;
    })
    .onComplete(() => {
      obj.position.set(target.x, target.y, target.z);
      obj.rotation.z = targetRotZ;
    })
    .start();
}

export function updateTweens(timeMs: number) {
  group.update(timeMs);
}

export function clearTweens() {
  group.removeAll();
}

export function finishAllTweens() {
  const all = group.getAll().slice();
  for (const tween of all) tween.end();
}

export function hasActiveTweens(): boolean {
  return group.getAll().length > 0;
}
